import p2, { Body, Constraint, Spring } from "p2";

import { PersistenceLevel } from "../../config/PersistenceLevels";
import { EntityDef } from "../EntityDef";
import Game from "../Game";
import { clamp } from "../util/MathUtil";
import { shapeFromDef } from "../util/PhysicsUtils";
import { V, V2d } from "../Vector";
import Entity, { GameEvents } from "./Entity";
import { GameSprite, spriteFromDef } from "./GameSprite";

/**
 * Base implementation of the Entity interface that provides common functionality
 * for all game objects. Most game entities should extend this class.
 *
 * ## Core Responsibilities
 *
 * BaseEntity handles the fundamental entity lifecycle and provides utilities for:
 * - **Parent-child relationships**: Hierarchical entity organization
 * - **Coordinate transformations**: Converting between local/world/screen spaces
 * - **Timer management**: Async waiting and scheduling systems
 * - **Event dispatching**: Convenient access to the game's event system
 * - **Resource management**: Loading from EntityDef configurations
 *
 * ## Parent-Child Relationships
 *
 * Entities can form hierarchies where child entities are automatically managed:
 *
 * ```typescript
 * class Turret extends BaseEntity {
 *   constructor() {
 *     super();
 *     // Create child entities for turret parts
 *     const base = new TurretBase();
 *     const barrel = new TurretBarrel();
 *     this.addChild(base);
 *     this.addChild(barrel);
 *   }
 * }
 * ```
 *
 * **Child Entity Benefits:**
 * - Automatically added to game when parent is added
 * - Automatically destroyed when parent is destroyed
 * - Inherit parent's persistence level by default
 * - Can access parent via `this.parent` property
 *
 * ## Coordinate Transformations
 *
 * BaseEntity provides methods to convert between coordinate spaces:
 * - **World Space**: Physics world coordinates (meters from origin)
 * - **Local Space**: Coordinates relative to entity's position/rotation
 *
 * ```typescript
 * // Convert local point to world coordinates
 * const worldPos = this.localToWorld([10, 0]); // 10 units right of entity
 *
 * // Convert world point to local coordinates
 * const localPos = this.worldToLocal(targetPosition);
 * ```
 *
 * ## Timer System
 *
 * BaseEntity provides powerful async timing utilities:
 *
 * ```typescript
 * // Wait for specific duration
 * await this.wait(2.0); // Wait 2 seconds
 *
 * // Wait with progress callback
 * await this.wait(1.0, (dt, progress) => {
 *   this.alpha = progress; // Fade in over 1 second
 * });
 *
 * // Wait until condition is met
 * await this.waitUntil(() => this.health <= 0);
 *
 * // Wait for render frames (not game ticks)
 * await this.waitRender(0.5); // Wait 0.5 seconds of render time
 * ```
 *
 * **Timer Management:**
 * - `clearTimers()`: Cancel all active timers
 * - `clearTimers("timerName")`: Cancel specific named timer
 * - `updateTimers(0.5)`: Set all timers to 0.5 seconds remaining
 *
 * ## Event Dispatching
 *
 * Easily dispatch events to all entities in the game:
 *
 * ```typescript
 * // Dispatch custom events
 * this.dispatch("enemyKilled", { enemy: this, score: 100 });
 *
 * // Dispatch with pause respect
 * this.dispatch("gameEvent", data, true); // Respects pause state
 * ```
 *
 * ## Usage Examples
 *
 * ```typescript
 * class Enemy extends BaseEntity {
 *   constructor() {
 *     super();
 *     this.id = "enemy-" + Math.random(); // Optional unique ID
 *     this.tags = ["enemy", "destructible"]; // Tags for queries
 *
 *     // Set up physics
 *     this.body = new Body({ mass: 1.0 });
 *     this.body.addShape(new Circle({ radius: 10 }));
 *
 *     // Set up rendering
 *     this.sprite = createGraphics("main").circle(0, 0, 10);
 *   }
 *
 *   async onAdd() {
 *     // Called when added to game
 *     await this.wait(5.0); // Live for 5 seconds
 *     this.destroy();
 *   }
 *
 *   onTick(dt: number) {
 *     // Update logic every game tick
 *     if (this.health <= 0) {
 *       this.dispatch("enemyKilled", { enemy: this });
 *       this.destroy();
 *     }
 *   }
 * }
 * ```
 */
export default abstract class BaseEntity implements Entity {
  bodies?: p2.Body[];
  body?: p2.Body;
  children: Entity[] = [];
  constraints?: Constraint[];
  game: Game | undefined = undefined;
  parent?: Entity;
  pausable: boolean = true;
  persistenceLevel: PersistenceLevel = PersistenceLevel.Level;
  springs?: Spring[];
  id?: string;
  tags: string[] = [];
  sprite?: GameSprite;
  sprites?: GameSprite[];

  constructor(entityDef?: EntityDef) {
    if (entityDef) {
      this.loadFromDef(entityDef);
    }
  }

  loadFromDef(def: EntityDef): void {
    if (this.game) {
      throw new Error(
        "Can't load from def after entity has been added to game.",
      );
    }

    if (def.sprites) {
      if (def.sprites.length === 1) {
        this.sprite = spriteFromDef(def.sprites[0]);
      } else {
        this.sprites = def.sprites.map((spriteDef) => spriteFromDef(spriteDef));
      }
    }

    if (def.body) {
      this.body = new Body({ mass: def.body.mass });
      for (const shapeDef of def.body.shapes) {
        const shape = shapeFromDef(shapeDef);
        this.body.addShape(shape, shape.position, shape.angle);
      }
    }
  }

  /**
   * Convert local coordinates to world coordinates.
   *
   * Local coordinates are relative to this entity's position and rotation.
   * World coordinates are absolute positions in the physics world.
   *
   * Requires this entity to have a physics body for the transformation.
   *
   * @param localPoint Point in local space relative to entity center
   * @returns Point in world space coordinates
   *
   * @example
   * // Get world position of a point 10 units to the right of entity
   * const rightOfEntity = this.localToWorld([10, 0]);
   *
   * // Get world position of entity's "gun barrel" offset
   * const barrelTip = this.localToWorld([0, -30]); // 30 units up from center
   */
  localToWorld(localPoint: [number, number]): V2d {
    if (this.body) {
      const result: V2d = V(0, 0);
      this.body.toWorldFrame(result, localPoint);
      return result;
    }
    return V(0, 0);
  }

  /**
   * Convert world coordinates to local coordinates.
   *
   * World coordinates are absolute positions in the physics world.
   * Local coordinates are relative to this entity's position and rotation.
   *
   * Requires this entity to have a physics body for the transformation.
   *
   * @param worldPoint Point in world space coordinates
   * @returns Point in local space relative to entity center
   *
   * @example
   * // Convert mouse position to local entity coordinates
   * const mouseWorld = game.camera.screenToWorld(mousePosition);
   * const mouseLocal = this.worldToLocal(mouseWorld);
   *
   * // Check if a world point is "in front" of this entity
   * const targetLocal = this.worldToLocal(target.getPosition());
   * const isInFront = targetLocal.y < 0; // Negative Y is "up" in local space
   */
  worldToLocal(worldPoint: [number, number]): V2d {
    if (this.body) {
      const result: V2d = V(0, 0);
      this.body.toLocalFrame(result, worldPoint);
      return result;
    }
    return V(0, 0);
  }

  /**
   * Get the current world position of this entity.
   *
   * Returns the position from the physics body if available, otherwise
   * falls back to the sprite position. Throws an error if neither exists.
   *
   * @returns Current world position as a Vector
   *
   * @example
   * // Get distance between two entities
   * const distance = this.getPosition().distance(target.getPosition());
   *
   * // Move towards a target
   * const direction = target.getPosition().sub(this.getPosition()).normalize();
   * this.body.velocity = direction.imul(speed);
   */
  getPosition(): V2d {
    if (this.body) {
      return V(this.body.position);
    } else if (this.sprite) {
      return V(this.sprite.position.x, this.sprite.position.y);
    }
    throw new Error("Position is not implemented for this entity");
  }

  isDestroyed(): this is this & { game: undefined } {
    return this.game == undefined;
  }

  isAlive(): this is this & { game: Game } {
    return this.game != undefined;
  }

  // Removes this from the game. You probably shouldn't override this method.
  destroy() {
    if (this.game) {
      this.game.removeEntity(this);
      while (this.children?.length) {
        this.children[this.children.length - 1].destroy();
      }
      if (this.parent) {
        const pChildren = this.parent.children!;
        const index = pChildren.lastIndexOf(this);
        if (index < 0) {
          throw new Error(`Parent doesn't have child`);
        }
        pChildren.splice(index, 1);
      }
    }
  }

  /**
   * Add another entity as a child of this entity.
   *
   * Child entities are automatically managed by their parent:
   * - Added to game when parent is added (if parent is already in game)
   * - Destroyed when parent is destroyed
   * - Can access parent via child.parent property
   *
   * This is useful for creating composite entities (turret with base + barrel),
   * temporary effects (explosion with multiple particle systems), or any
   * hierarchical organization.
   *
   * @param child Entity to add as a child
   * @param changeParent If true, moves child from existing parent to this one
   * @returns The child entity for chaining
   *
   * @example
   * class Turret extends BaseEntity {
   *   constructor() {
   *     super();
   *
   *     // Create child components
   *     const base = new TurretBase();
   *     const barrel = new TurretBarrel();
   *
   *     // Add as children - they'll be destroyed with the turret
   *     this.addChild(base);
   *     this.addChild(barrel);
   *   }
   * }
   *
   * // Chaining example
   * this.addChild(new Effect()).addChild(new Sound()).addChild(new Particle());
   */
  addChild<T extends Entity>(child: T, changeParent: boolean = false): T {
    if (child.parent) {
      if (changeParent) {
        // This can lead to weird state where a child is added but its parent isn't, dunno if that's bad
        const oldParent = child.parent;
        oldParent.children!.splice(oldParent.children!.indexOf(child), 1);
      } else {
        throw new Error("Child already has a parent.");
      }
    }
    child.parent = this;
    this.children = this.children ?? [];
    this.children.push(child);

    if (this.game && !child.game) {
      this.game.addEntity(child);
    }
    return child;
  }

  /**
   * Add multiple entities as children of this entity.
   *
   * Convenience method for adding multiple children at once.
   * Each child is added using addChild() with default parameters.
   *
   * @param children Entities to add as children
   *
   * @example
   * // Add multiple child entities at once
   * this.addChildren(
   *   new TurretBase(),
   *   new TurretBarrel(),
   *   new TurretMuzzleFlash()
   * );
   */
  addChildren(...children: readonly Entity[]): void {
    for (const child of children) {
      this.addChild(child);
    }
  }

  /**
   * Fulfills after the given amount of game time.
   * Use with delay=0 to wait until the next tick.
   * @param onTick  Do something every tick while waiting
   */
  wait(
    delay: number = 0,
    onTick?: (dt: number, t: number) => void,
    timerId?: string,
  ): Promise<void> {
    return new Promise((resolve) => {
      const timer = new Timer(delay, () => resolve(), onTick, timerId);
      timer.persistenceLevel = this.persistenceLevel;
      this.addChild(timer);
    });
  }

  /**
   * Fulfills after the given amount of game time.
   * Use with delay=0 to wait until the next tick.
   * @param onRender  Do something every render while waiting
   */
  waitRender(
    delay: number = 0,
    onRender?: (dt: number, t: number) => void,
    timerId?: string,
  ): Promise<void> {
    return new Promise((resolve) => {
      const timer = new RenderTimer(delay, () => resolve(), onRender, timerId);
      timer.persistenceLevel = this.persistenceLevel;
      this.addChild(timer);
    });
  }

  /**
   * Wait until a condition is filled. Probably not great to use, but seems kinda cool too.
   */
  waitUntil(
    predicate: () => boolean,
    onTick?: (dt: number, t: number) => void,
    timerId?: string,
  ): Promise<void> {
    return new Promise((resolve) => {
      const timer = new Timer(
        Infinity,
        () => resolve(),
        (dt, t) => {
          if (onTick) {
            onTick(dt, t);
          }
          if (predicate()) {
            timer.timeRemaining = 0;
          }
        },
        timerId,
      );
      timer.persistenceLevel = this.persistenceLevel;
      this.addChild(timer);
    });
  }

  /**
   * Remove all timers from this instance. i.e. cancel all 'waits'.
   */
  clearTimers(timerId?: string): void {
    if (this.children) {
      const timers = this.children.filter(isTimer);
      for (const timer of timers) {
        if (!timerId || timerId === timer.timerId) {
          timer.destroy();
        }
      }
    }
  }

  /**
   * Update the time remaing on a timer (or all timers).
   */
  updateTimers(value: number = 0, timerId?: string): void {
    if (this.children) {
      const timers = this.children.filter(isTimer);
      for (const timer of timers) {
        if (!timerId || timerId === timer.timerId) {
          timer.timeRemaining = value;
        }
      }
    }
  }

  /**
   * Dispatch an event to all entities in the game.
   *
   * This is a convenience method that calls game.dispatch(). Events are sent
   * to all entities that have a matching onEventName handler method.
   *
   * @param eventName Name of the event to dispatch
   * @param data Event data to send to handlers
   * @param respectPause If true (default), pausable entities won't receive event when paused
   *
   * @example
   * // Dispatch score event when enemy is killed
   * this.dispatch("scoreAdded", { points: 100, source: this });
   *
   * // Dispatch level complete event
   * this.dispatch("levelComplete", { level: this.currentLevel });
   *
   * // Dispatch event that ignores pause state
   * this.dispatch("gameStateChanged", { state: "paused" }, false);
   */
  dispatch<EventName extends keyof GameEvents>(
    eventName: EventName,
    data: GameEvents[EventName],
    respectPause?: boolean,
  ) {
    this.game?.dispatch(eventName, data, respectPause);
  }
}

class Timer extends BaseEntity implements Entity {
  timeRemaining: number = 0;
  endEffect?: () => void;
  duringEffect?: (dt: number, t: number) => void;

  constructor(
    private delay: number,
    endEffect?: () => void,
    duringEffect?: (dt: number, t: number) => void,
    public timerId?: string,
  ) {
    super();
    this.timeRemaining = delay;
    this.endEffect = endEffect;
    this.duringEffect = duringEffect;
  }

  onTick(dt: number) {
    this.timeRemaining -= dt;
    const t = clamp(1.0 - this.timeRemaining / this.delay);
    this.duringEffect?.(dt, t);
    if (this.timeRemaining <= 0) {
      this.endEffect?.();
      this.destroy();
    }
  }
}

class RenderTimer extends BaseEntity implements Entity {
  timeRemaining: number = 0;
  endEffect?: () => void;
  duringEffect?: (dt: number, t: number) => void;

  constructor(
    private delay: number,
    endEffect?: () => void,
    duringEffect?: (dt: number, t: number) => void,
    public timerId?: string,
  ) {
    super();
    this.timeRemaining = delay;
    this.endEffect = endEffect;
    this.duringEffect = duringEffect;
  }

  onRender(dt: number) {
    this.timeRemaining -= dt;
    const t = clamp(1.0 - this.timeRemaining / this.delay);
    this.duringEffect?.(dt, t);
    if (this.timeRemaining <= 0) {
      this.endEffect?.();
      this.destroy();
    }
  }
}

function isTimer(e?: Entity): e is Timer {
  return e instanceof Timer;
}
